import { parse, simplify } from 'mathjs'

export default class EquationFitter {
  constructor(data, inputs, outputVar) {
    this.operations = [
      '+', '-', '*', '/', '**',
      '+', '-', '*', '/', '**',
      'Math.sqrt', 'Math.log',
    ]
    this.data = data
    this.inputs = inputs
    this.topResults = []
    this.outputVar = outputVar
  }

  generateRandomEquation() {
    let inputs = [...this.inputs]
    const getInput = () => {
      let input
      if (Math.random() > 0.7) {
        input = inputs[Math.floor(Math.random() * inputs.length)]
      } else {
        const idx = Math.floor(Math.random() * inputs.length)
        input = inputs.splice(idx, 1)[0]
      }
      if (Math.random() > 0.8) {
        const exponent = Math.floor(Math.random() * 2) + 2
        input = `Math.pow(${input}, ${exponent})`
      }
      return input
    }
    const getConstant = () => {
      let constant
      if (Math.random() > 0.2) {
        constant = (Math.random() * 10).toFixed(1)
      } else if (Math.random() > 0.5) {
        const mathConstants = ['Math.PI', 'Math.E']
        constant = mathConstants[Math.floor(Math.random() * mathConstants.length)]
      } else {
        constant = (Math.random() * 100).toFixed(1)
      }
      return constant
    }

    let eq = getInput()
    const numTerms = Math.floor(Math.random() * 20) + inputs.length

    for (let i = 0; i < numTerms && (inputs.length || Math.random() > 0.3); i++) {
      const operation = this.operations[Math.floor(Math.random() * this.operations.length)]
      if (['+', '-', '*', '/'].includes(operation)) {
        if (inputs.length) {
          let input = getInput()
          if (Math.random() > 0.5) {
            const coeff = getConstant()
            input = `(${coeff} * ${input})`
          }
          eq = `(${eq} ${operation} ${input})`
        }
      } else if (operation === '**') {
        const exponent = Math.floor(Math.random() * 2) + 2
        eq = `Math.pow(${eq}, ${exponent})`
      } else if (eq.slice(0, 4) !== 'Math' && eq.slice(0, 5) !== '(Math') {
        eq = `${operation}(${eq})`
      }
    }

    return eq
  }

  evaluateEquation(equation) {
    const keys = Object.keys(this.data[0])
    // eslint-disable-next-line no-new-func
    const evalEquation = new Function(...keys, `return ${equation}`)
    return this.data.map(row => evalEquation(...keys.map(key => row[key])))
  }

  leastSquaresError(yTrue, yPred) {
    return yTrue.reduce((sum, trueVal, idx) => sum + Math.pow(trueVal - yPred[idx], 2), 0) / yTrue.length
  }

  averageError(yTrue, yPred) {
    return yTrue.reduce((sum, trueVal, idx) => sum + trueVal - yPred[idx], 0) / yTrue.length
  }

  averageAbsError(yTrue, yPred) {
    return yTrue.reduce((sum, trueVal, idx) => sum + trueVal - yPred[idx], 0) / yTrue.length
  }

  rSquared(yTrue, yPred) {
    let sumX = 0
    let sumY = 0
    let sumXY = 0
    let sumX2 = 0
    let sumY2 = 0
    let n = yTrue.length

    for (let i = 0; i < yTrue.length; i++) {
      let x = yTrue[i]
      let y = yPred[i]
      sumX += x
      sumY += y
      sumXY += x * y
      sumX2 += x * x
      sumY2 += y * y
    }

    let meanX = sumX / n
    let meanY = sumY / n

    let m = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
    let b = meanY - m * meanX

    let ssTot = sumY2 - (sumY * sumY) / n
    let ssRes = sumY2 - m * sumXY - b * sumY
    let rSquared = 1 - (ssRes / ssTot)

    return rSquared
  }

  findBestScaleForEquation(equation) {
    const yTrue = this.data.map(row => row[this.outputVar])
    const scales = []

    for (let scale = 0.1; scale <= 10; scale += 0.1) scales.push(scale)
    for (let scale = 10; scale <= 100; scale += 1) scales.push(scale)

    const yPred = this.evaluateEquation(equation, this.data)
    const r2 = this.rSquared(yTrue, yPred)
    let minScale = .01
    let maxScale = 1000
    let error
    let scale
    let yPredScaled
    let maxSteps = 50
    while (maxSteps-- > 0 && Math.abs(maxScale - minScale) > 0.01) {
      scale = (maxScale + minScale) / 2 // binary search
      yPredScaled = yPred.map(p => p * scale)
      error = this.averageError(yTrue, yPredScaled)
      if (error < 0) {
        maxScale = scale
      } else {
        minScale = scale
      }
    }
    const scaledEquation = `(${scale.toFixed(2)} * ${equation})`
    const leastSquaresError = this.leastSquaresError(yTrue, yPredScaled)

    this.updateTopResults(scaledEquation, leastSquaresError, r2, yPredScaled)
  }

  findBestEquation(numIterations = 1000000) {
    console.log('Finding best equation...')
    const yTrue = this.data.map(row => row[this.outputVar])
    this.yTrue = yTrue
    let eq
    for (let i = 0; i < numIterations; i++) {
      try {
        eq = this.generateRandomEquation()
        const yPred = this.evaluateEquation(eq, this.data)
        const r2 = this.rSquared(yTrue, yPred)

        if (r2 > 0 && r2 <= 1) {
          this.findBestScaleForEquation(eq)
        }
      } catch (error) {
        console.log(`Error evaluating equation ${eq}:`, error, )
        continue
      }
    }

    return this.topResults
  }

  updateTopResults(eq, error, r2, data, debug = false) {
    const topCount = 20
    //const score = error * (1 - r2)
    const score = error
    if (this.topResults.length < topCount || score < this.topResults[this.topResults.length - 1].score) {
      const simplified = this.simplifyExpression(eq)
      const result = { eq, error, r2, data, simplified, score }
      result.avgError = this.averageAbsError(this.yTrue, data)
      this.topResults.push(result)
      this.topResults.sort((a, b) => a.score - b.score)

      if (this.topResults.length > topCount) {
        this.topResults.pop()
      }

      if (debug) {
        for (let i = this.topResults.length - 1; i >= 0; --i) {
          console.log(`${i + 1}: ${this.topResults[i].r2.toFixed(3)}, ${this.topResults[i].error.toFixed(3)}, ${this.topResults[i].simplified}`)
        }
        console.log("\n\nnew best Equation:", this.topResults[0].eq)
        console.log(this.simplifyExpression(this.topResults[0].eq))
        console.log("new least Squares Error:", this.topResults[0].error)
        console.log("new best R^2:", this.topResults[0].r2)
        console.log("Predictions:", this.topResults[0].data.map(p => p.toFixed(1)).join(","))
        console.log("Actual:", this.data.map(row => row[this.outputVar]).join(","))
      }
    }
  }

  simplifyExpression(expression) {
    try {
      const mathJsExpression = expression.replace(/Math\./g, 'math.')
      const node = parse(mathJsExpression)
      const simplified = simplify(node)
      return simplified.toString()
    } catch (error) {
      console.error('Error simplifying expression:', error)
      return expression
    }
  }
}
